import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResourceCard } from '../ResourceCard';
import Icon from '@components/Icon/Icon';
import { BaseModal } from '@components/modals';
import { useLanguage } from '@contexts/LanguageContext';
import UserCard from './UserCard';
import CategoryCard from './CategoryCard';
import LocationCard from './LocationCard';
import TagCard from './TagCard';
import GenericCard from './GenericCard';
import api from '@services/api';
import BorrowingCard from './BorrowingCard';
import ConsumptionCard from './ConsumptionCard';
import RefillCard from './RefillCard';
import ReservationCard from './ReservationCard';
import './ModelCard.scss';

/**
 * ModelCard component with bulk permission-based action visibility
 *
 * This component displays model cards with permission-based action visibility using
 * bulk permissions passed as props. This is more efficient than individual permission checks.
 *
 * @param {Object} model - The model object to display (must include model_type property)
 * @param {string} displayType - Display mode ('grid' or 'list')
 * @param {Function} onClick - Click handler for the card
 * @param {Function} onRefresh - Refresh handler after actions
 * @param {Array} actions - Custom actions to override standard edit/delete
 * @param {Object} permissions - Permissions object from bulk permissions (required)
 * @param {boolean} permissions.canUpdate - Whether user can update this model
 * @param {boolean} permissions.canDelete - Whether user can delete this model
 */

export const ModelCard = React.memo(({
    model,
    displayType = 'grid',
    onClick,
    onRefresh,
    actions,
    permissions = {},
    displayOptions = {}
}) => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Get permissions from props using memoization for performance
    const { canEdit, canDelete } = useMemo(() => {
        // If no model ID, deny all permissions
        if (!model?.id) {
            return { canEdit: false, canDelete: false };
        }

        // Use simplified permissions structure - just direct boolean values
        return {
            canEdit: permissions.update || permissions.canUpdate || false,
            canDelete: permissions.delete || permissions.canDelete || false
        };
    }, [
        model?.id,
        permissions.update,
        permissions.canUpdate,
        permissions.delete,
        permissions.canDelete,
    ]);

    const handleClick = () => {
        if (onClick) {
            onClick(model);
        }
    };

    const handleEdit = (e) => {
        e.stopPropagation();

        // Check if user has edit permission
        if (!canEdit) {
            return;
        }

        // Navigate to edit page for all model types
        switch (model.model_type) {
            case 'category':
                navigate(`/categories/${model.id}/edit`);
                break;
            case 'location':
                navigate(`/locations/${model.id}/edit`);
                break;
            case 'tag':
                navigate(`/tags/${model.id}/edit`);
                break;
            case 'resource':
                navigate(`/resources/${model.id}/edit`);
                break;
            default:
                // For other model types, use modal
                setShowEditModal(true);
                break;
        }
    };

    const handleDelete = (e) => {
        e.stopPropagation();

        // Check if user has delete permission
        if (!canDelete) {
            return;
        }

        setShowDeleteModal(true);
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setShowDeleteModal(false);
    };

    const handleSuccess = () => {
        onRefresh?.();
        handleCloseModal();
    };

    // Render custom actions if provided, otherwise render standard actions
    const renderActions = () => {
        if (actions && actions.length > 0) {
            return displayType === 'list' ? (
                <div className="model-actions">
                    {actions.map((action, index) => {
                        // Check if action should be shown for this model
                        if (action.condition && !action.condition(model)) {
                            return null;
                        }

                        return (
                            <button
                                key={index}
                                className={`btn btn-sm btn-${action.variant || 'primary'}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    action.onClick(model);
                                }}
                                title={action.label}
                            >
                                {action.icon && <Icon icon={action.icon} className="btn-icon" />}
                                <span className="btn-text">{action.label}</span>
                            </button>
                        );
                    })}
                </div>
            ) : (
                <div className="model-actions">
                    {actions.map((action, index) => {
                        // Check if action should be shown for this model
                        if (action.condition && !action.condition(model)) {
                            return null;
                        }

                        return (
                            <Icon
                                key={index}
                                icon={action.icon || 'cog'}
                                className={`btn-icon icon--${action.variant || 'primary'} icon--clickable`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    action.onClick(model);
                                }}
                                title={action.label}
                            />
                        );
                    })}
                </div>
            );
        }

        // Fallback to standard actions
        return renderStandardActions();
    };

    // Render standard actions for editable models
    const renderStandardActions = () => {
        if (['resource','category', 'location', 'tag'].includes(model.model_type)) {
            const actions = [];

            // Add edit action if user has permission
            if (canEdit) {
                actions.push({
                    type: 'edit',
                    button: displayType === 'list' ? (
                        <button
                            key="edit"
                            className="btn btn-sm btn-primary"
                            onClick={handleEdit}
                            title={t('common.edit')}
                        >
                            <Icon icon="edit" className="btn-icon" />
                            <span className="btn-text">{t('common.edit')}</span>
                        </button>
                    ) : (
                        <Icon
                            key="edit"
                            icon="edit"
                            className="btn-icon icon--primary icon--clickable"
                            onClick={handleEdit}
                            title={t('common.edit')}
                        />
                    )
                });
            }

            // Add delete action if user has permission
            if (canDelete) {
                actions.push({
                    type: 'delete',
                    button: displayType === 'list' ? (
                        <button
                            key="delete"
                            className="btn btn-sm btn-danger"
                            onClick={handleDelete}
                            title={t('common.delete')}
                        >
                            <Icon icon="trash" className="btn-icon" />
                            <span className="btn-text">{t('common.delete')}</span>
                        </button>
                    ) : (
                        <Icon
                            key="delete"
                            icon="trash"
                            className="btn-icon icon--danger icon--clickable"
                            onClick={handleDelete}
                            title={t('common.delete')}
                        />
                    )
                });
            }

            // Return actions container if there are any actions
            if (actions.length > 0) {
                return (
                    <div className="model-actions">
                        {actions.map(action => action.button)}
                    </div>
                );
            }
        }
        return null;
    };

    // Render the appropriate modal based on model type
    const renderModal = () => {
        // Only render delete modal if user has delete permission
        if (!canDelete) {
            return null;
        }

        switch (model.model_type) {
            case 'category':
                return (
                    <>
                        {showDeleteModal && (
                            <BaseModal
                                isOpen={showDeleteModal}
                                onClose={handleCloseModal}
                                onSuccess={handleSuccess}
                                mode="delete"
                                item={model}
                                itemType="category"
                                deleteFn={async (id) => await api.delete(`/categories/${id}`)}
                                size="small"
                            />
                        )}
                    </>
                );
            case 'location':
                return (
                    <>
                        {showDeleteModal && (
                            <BaseModal
                                isOpen={showDeleteModal}
                                onClose={handleCloseModal}
                                onSuccess={handleSuccess}
                                mode="delete"
                                item={model}
                                itemType="location"
                                deleteFn={async (id) => await api.delete(`/locations/${id}`)}
                                size="small"
                            />
                        )}
                    </>
                );
            case 'tag':
                return (
                    <>
                        {showDeleteModal && (
                            <BaseModal
                                isOpen={showDeleteModal}
                                onClose={handleCloseModal}
                                onSuccess={handleSuccess}
                                mode="delete"
                                item={model}
                                itemType="tag"
                                deleteFn={async (id) => await api.delete(`/tags/${id}`)}
                                size="small"
                            />
                        )}
                    </>
                );
            case 'resource':
                return (
                    <>
                        {showDeleteModal && (
                            <BaseModal
                                isOpen={showDeleteModal}
                                onClose={handleCloseModal}
                                onSuccess={handleSuccess}
                                mode="delete"
                                item={model}
                                itemType="resource"
                                deleteFn={async (id) => await api.delete(`/resources/${id}`)}
                                size="small"
                            />
                        )}
                    </>
                );
            default:
                return null;
        }
    };

    // Switch based on model type to render appropriate card
    const renderCard = () => {
        const actionsToRender = renderActions();

        switch (model.model_type) {
            case 'resource':
                return (
                    <ResourceCard
                        resource={model}
                        displayType={displayType}
                        displayOptions={displayOptions}
                        onClick={handleClick}
                        actions={actionsToRender}
                    />
                );

            case 'user':
                return (
                    <UserCard
                        user={model}
                        displayType={displayType}
                        displayOptions={displayOptions}
                        onClick={handleClick}
                    />
                );

            case 'category':
                return (
                    <CategoryCard
                        category={model}
                        displayType={displayType}
                        displayOptions={displayOptions}
                        onClick={handleClick}
                        actions={actionsToRender}
                    />
                );

            case 'location':
                return (
                    <LocationCard
                        location={model}
                        displayType={displayType}
                        displayOptions={displayOptions}
                        onClick={handleClick}
                        actions={actionsToRender}
                    />
                );

            case 'tag':
                return (
                    <TagCard
                        tag={model}
                        displayType={displayType}
                        displayOptions={displayOptions}
                        onClick={handleClick}
                        actions={actionsToRender}
                    />
                );

            case 'borrowing':
                return (
                    <BorrowingCard
                        borrowing={model}
                        displayType={displayType}
                        displayOptions={displayOptions}
                        onClick={handleClick}
                        actions={actionsToRender}
                    />
                );

            case 'consumption':
                return (
                    <ConsumptionCard
                        consumption={model}
                        displayType={displayType}
                        displayOptions={displayOptions}
                        onClick={handleClick}
                        actions={actionsToRender}
                    />
                );

            case 'refill':
                return (
                    <RefillCard
                        refill={model}
                        displayType={displayType}
                        displayOptions={displayOptions}
                        onClick={handleClick}
                        actions={actionsToRender}
                    />
                );

            case 'reservation':
                return (
                    <ReservationCard
                        reservation={model}
                        displayType={displayType}
                        displayOptions={displayOptions}
                        onClick={handleClick}
                        actions={actionsToRender}
                    />
                );

            default:
                return (
                    <GenericCard
                        model={model}
                        modelType={model.model_type}
                        displayOptions={displayOptions}
                        displayType={displayType}
                        onClick={handleClick}
                        actions={actionsToRender}
                    />
                );
        }
    };

    return (
        <div className={`model-card model-card--${model.model_type} model-card--${displayType}`}>
            {renderCard()}
            {renderModal()}
        </div>
    );
});

export default ModelCard;